import * as React from 'react';
import { compose } from 'redux';

import type { FieldReportFullViewFilterContextType } from 'af-root/context/fieldReportFullViewFilterContext';

import type { UserTypeFilterOption } from 'ab-enums/userTypeFilter.enum';

import { ADDITIONAL_FILTERS, ADDITIONAL_FILTERS_OPTIONS, MAIN_FILTER_OPTION_IDS } from '..';
import AssignmentFilter from './AssignmentFilter';
import type { CrewTypeFilterOption } from './CrewTypeFilter';
import CrewTypeFilter from './CrewTypeFilter';
import type { JobFilterOption } from './JobFilter';
import JobFilter from './JobFilter';
import type { ProjectManagerFilterOption } from './ProjectManagerFilter';
import ProjectManagerFilter from './ProjectManagerFilter';
import type { ReviewStatusFilterOption } from './ReviewStatusFilter';
import ReviewStatusFilter from './ReviewStatusFilter';
import Checkbox from './Shared/Checkbox';
import styles from './styles.module.scss';
import type { SuperintendentFilterOption } from './SuperintendentFilter';
import SuperintendentFilter from './SuperintendentFilter';
import type { WorkSummaryStatusFilterOption } from './WorkSummaryStatusFilter';
import WorkSummaryStatusFilter from './WorkSummaryStatusFilter';

interface OwnProps {
	changeAssignmentsFilter: (filter: UserTypeFilterOption) => void;
	changeCrewTypeFilters: (crewTypeOptions: CrewTypeFilterOption[]) => void;
	changeJobFilters: (jobOptions: JobFilterOption[]) => void;
	changeProjectManagerFilters: (projectManagerOptions: ProjectManagerFilterOption[]) => void;
	changeReviewStatusFilters: (reviewStatusOptions: ReviewStatusFilterOption[]) => void;
	changeSuperintendentFilters: (superintendentOptions: SuperintendentFilterOption[]) => void;
	selectedCrewTypeFilters: CrewTypeFilterOption[];
	selectedJobFilters: JobFilterOption[];
	selectedProjectManagerFilters: ProjectManagerFilterOption[];
	selectedSuperintendentFilters: SuperintendentFilterOption[];
	selectedMainFilterId: number;
	requiresMyAttention: boolean;
	excludeCancelled: boolean;
	assignmentFilter: Nullable<UserTypeFilterOption>;
	changeRequiresMyAttention: (event: React.ChangeEvent<HTMLInputElement>) => void;
	changeExcludeCancelled: (event: React.ChangeEvent<HTMLInputElement>) => void;
	changeWorkSummaryStatusFilters: (statusOptions: WorkSummaryStatusFilterOption[]) => void;
	selectedWorkSummaryStatusFilters: WorkSummaryStatusFilterOption[];
	selectedReviewStatusFilters: ReviewStatusFilterOption[];
	fieldReportFullViewFilterContext: FieldReportFullViewFilterContextType;
}

type Props = OwnProps;

const BulkModalFilter: React.FC<Props> = (props) => {

	const {
		changeAssignmentsFilter,
		changeCrewTypeFilters,
		changeJobFilters,
		changeProjectManagerFilters,
		changeReviewStatusFilters,
		changeSuperintendentFilters,
		selectedCrewTypeFilters,
		selectedJobFilters,
		selectedProjectManagerFilters,
		selectedSuperintendentFilters,
		selectedMainFilterId,
		requiresMyAttention,
		assignmentFilter,
		changeRequiresMyAttention,
		changeWorkSummaryStatusFilters,
		selectedWorkSummaryStatusFilters,
		selectedReviewStatusFilters,
		fieldReportFullViewFilterContext,
		excludeCancelled,
		changeExcludeCancelled,
	} = props;

	const renderFilters = React.useCallback(() => {
		return ADDITIONAL_FILTERS_OPTIONS.map((filter) => {
			let filterName = '';
			let body = <div />;
			switch (filter.id) {
				case ADDITIONAL_FILTERS.JOB:
					if (selectedMainFilterId === MAIN_FILTER_OPTION_IDS.JOB) {
						return null;
					}
					filterName = 'Job ID';
					body =
						<JobFilter
							fieldReportFullViewFilterContext={fieldReportFullViewFilterContext}
							forBulkModal={true}
							key={ADDITIONAL_FILTERS.JOB}
							onChange={changeJobFilters}
							selected={selectedJobFilters}
						/>;
					break;
				case ADDITIONAL_FILTERS.PROJECT_MANAGER:
					filterName = 'Project Manager';
					body =
						<ProjectManagerFilter
							fieldReportFullViewFilterContext={fieldReportFullViewFilterContext}
							forBulkModal={true}
							key={ADDITIONAL_FILTERS.PROJECT_MANAGER}
							onChange={changeProjectManagerFilters}
							selected={selectedProjectManagerFilters}
						/>;
					break;
				case ADDITIONAL_FILTERS.SUPERINTENDENT:
					filterName = 'Superintendent';
					body =
						<SuperintendentFilter
							fieldReportFullViewFilterContext={fieldReportFullViewFilterContext}
							forBulkModal={true}
							key={ADDITIONAL_FILTERS.SUPERINTENDENT}
							onChange={changeSuperintendentFilters}
							selected={selectedSuperintendentFilters}
						/>;
					break;
				case ADDITIONAL_FILTERS.ASSIGNMENT:
					filterName = 'Assigned';
					body =
						<AssignmentFilter
							forBulkModal={true}
							key={ADDITIONAL_FILTERS.ASSIGNMENT}
							onChange={changeAssignmentsFilter}
							selected={assignmentFilter}
						/>;
					break;

				case ADDITIONAL_FILTERS.CREW_TYPE:
					filterName = 'Crew Type';
					body =
						<CrewTypeFilter
							fieldReportFullViewFilterContext={fieldReportFullViewFilterContext}
							forBulkModal={true}
							key={ADDITIONAL_FILTERS.CREW_TYPE}
							onChange={changeCrewTypeFilters}
							selected={selectedCrewTypeFilters}
						/>;
					break;
				case ADDITIONAL_FILTERS.REVIEW_STATUS:
					filterName = 'Review Status';
					body =
						<ReviewStatusFilter
							forBulkModal={true}
							key={ADDITIONAL_FILTERS.REVIEW_STATUS}
							onChange={changeReviewStatusFilters}
							selected={selectedReviewStatusFilters}
						/>;
					break;
				case ADDITIONAL_FILTERS.WORK_SUMMARY_STATUS:
					filterName = 'Work Summary Status';
					body =
						<WorkSummaryStatusFilter
							forBulkModal={true}
							key={ADDITIONAL_FILTERS.WORK_SUMMARY_STATUS}
							onChange={changeWorkSummaryStatusFilters}
							selected={selectedWorkSummaryStatusFilters}
						/>;
					break;
				case ADDITIONAL_FILTERS.REQUIRES_MY_ATTENTION:
					return (
						<div className={styles['bulk-modal-filter']} key={filter.id}>
							<Checkbox
								checked={requiresMyAttention}
								id="requiresMyAttention"
								label="Requires my Attention"
								labelClassName={styles['checkbox-label']}
								onChange={changeRequiresMyAttention}
							/>
						</div>
					);
				case ADDITIONAL_FILTERS.EXCLUDE_CANCELLED:
					return (
						<div className={styles['bulk-modal-filter']} key={filter.id}>
							<Checkbox
								checked={excludeCancelled}
								id="excludeCancelled"
								label="Exclude cancelled"
								labelClassName={styles['checkbox-label']}
								onChange={changeExcludeCancelled}
							/>
						</div>
					);
				default:
					return null;
			}

			return (
				<div className={styles['bulk-modal-filter']} key={filter.id}>
					<div className={styles['bulk-modal-filter-label']}>{filterName}</div>
					<div>{body}</div>
				</div>
			);
		});

	}, [
		changeAssignmentsFilter,
		changeCrewTypeFilters,
		changeJobFilters,
		changeProjectManagerFilters,
		changeReviewStatusFilters,
		changeSuperintendentFilters,
		selectedCrewTypeFilters,
		selectedJobFilters,
		selectedProjectManagerFilters,
		selectedSuperintendentFilters,
		selectedMainFilterId,
		requiresMyAttention,
		assignmentFilter,
		changeRequiresMyAttention,
		changeWorkSummaryStatusFilters,
		selectedWorkSummaryStatusFilters,
		selectedReviewStatusFilters,
		fieldReportFullViewFilterContext,
		excludeCancelled,
		changeExcludeCancelled,
	]);

	return (
		renderFilters()
	);
};

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo
);

export default enhance(BulkModalFilter);
